export type LatLngPair = [number, number];

export interface GeoBounds {
  minLat: number;
  maxLat: number;
  minLng: number;
  maxLng: number;
}

export type PixelBBox = [number, number, number, number];

const isFiniteNumber = (value: unknown): value is number =>
  typeof value === "number" && Number.isFinite(value);

/**
 * Parse an AiMappingRunArea.coordinates JSON string into [lat, lng] pairs.
 * Returns an empty array for missing/invalid payloads instead of throwing.
 */
export function parseAreaCoordinates(coordinates: string | null | undefined): LatLngPair[] {
  if (!coordinates) return [];
  try {
    const parsed: unknown = JSON.parse(coordinates);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (pair): pair is LatLngPair =>
        Array.isArray(pair) &&
        pair.length >= 2 &&
        isFiniteNumber(pair[0]) &&
        isFiniteNumber(pair[1])
    );
  } catch {
    return [];
  }
}

export function getAreaBounds(points: LatLngPair[]): GeoBounds | null {
  if (!points.length) return null;
  let minLat = Infinity;
  let maxLat = -Infinity;
  let minLng = Infinity;
  let maxLng = -Infinity;
  for (const [lat, lng] of points) {
    if (lat < minLat) minLat = lat;
    if (lat > maxLat) maxLat = lat;
    if (lng < minLng) minLng = lng;
    if (lng > maxLng) maxLng = lng;
  }
  return { minLat, maxLat, minLng, maxLng };
}

export function pixelPointToGeo(
  x: number,
  y: number,
  imageWidth: number,
  imageHeight: number,
  bounds: GeoBounds
): LatLngPair | null {
  if (!imageWidth || !imageHeight || imageWidth <= 0 || imageHeight <= 0) return null;
  const lng = bounds.minLng + (x / imageWidth) * (bounds.maxLng - bounds.minLng);
  const lat = bounds.maxLat - (y / imageHeight) * (bounds.maxLat - bounds.minLat);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  return [lat, lng];
}

export function pixelBBoxCenterToGeo(
  bbox: number[],
  imageWidth: number | null | undefined,
  imageHeight: number | null | undefined,
  bounds: GeoBounds | null
): LatLngPair | null {
  if (!bounds || !imageWidth || !imageHeight) return null;
  if (!Array.isArray(bbox) || bbox.length < 4) return null;
  const [x, y, width, height] = bbox;
  if (![x, y, width, height].every(isFiniteNumber)) return null;
  return pixelPointToGeo(x + width / 2, y + height / 2, imageWidth, imageHeight, bounds);
}

export function geoToPixel(
  lat: number,
  lng: number,
  imageWidth: number,
  imageHeight: number,
  bounds: GeoBounds
): [number, number] | null {
  if (!imageWidth || !imageHeight || imageWidth <= 0 || imageHeight <= 0) return null;
  const x = ((lng - bounds.minLng) / (bounds.maxLng - bounds.minLng)) * imageWidth;
  const y = ((bounds.maxLat - lat) / (bounds.maxLat - bounds.minLat)) * imageHeight;
  if (!Number.isFinite(x) || !Number.isFinite(y)) return null;
  return [x, y];
}

export function geoBBoxToPixel(
  points: LatLngPair[],
  imageWidth: number | null | undefined,
  imageHeight: number | null | undefined,
  bounds: GeoBounds | null
): PixelBBox | null {
  if (!bounds || !imageWidth || !imageHeight || points.length < 2) return null;
  const pointBounds = getAreaBounds(points);
  if (!pointBounds) return null;
  const topLeft = geoToPixel(
    pointBounds.maxLat,
    pointBounds.minLng,
    imageWidth,
    imageHeight,
    bounds
  );
  const bottomRight = geoToPixel(
    pointBounds.minLat,
    pointBounds.maxLng,
    imageWidth,
    imageHeight,
    bounds
  );
  if (!topLeft || !bottomRight) return null;
  return [topLeft[0], topLeft[1], bottomRight[0] - topLeft[0], bottomRight[1] - topLeft[1]];
}

export interface GeoBBox {
  southWest: LatLngPair;
  northEast: LatLngPair;
}

export function pixelBBoxToGeoBounds(
  bbox: number[],
  imageWidth: number | null | undefined,
  imageHeight: number | null | undefined,
  bounds: GeoBounds | null
): GeoBBox | null {
  if (!bounds || !imageWidth || !imageHeight) return null;
  if (!Array.isArray(bbox) || bbox.length < 4) return null;
  const [x, y, width, height] = bbox;
  if (![x, y, width, height].every(isFiniteNumber)) return null;
  const northWest = pixelPointToGeo(x, y, imageWidth, imageHeight, bounds);
  const southEast = pixelPointToGeo(x + width, y + height, imageWidth, imageHeight, bounds);
  if (!northWest || !southEast) return null;
  return {
    southWest: [southEast[0], northWest[1]],
    northEast: [northWest[0], southEast[1]],
  };
}

export function bboxCorners(points: LatLngPair[]): LatLngPair[] {
  const bounds = getAreaBounds(points);
  if (!bounds) return [];
  return [
    [bounds.minLat, bounds.minLng],
    [bounds.maxLat, bounds.maxLng],
  ];
}

export function validateAreaCoordinates(
  inputType: "BBOX" | "POLYGON",
  points: LatLngPair[]
): string | null {
  if (inputType === "BBOX" && points.length !== 2) {
    return "A bounding box needs exactly 2 corner points.";
  }
  if (inputType === "POLYGON" && points.length < 3) {
    return "A polygon needs at least 3 points.";
  }
  return null;
}

export function serializeAreaCoordinates(points: LatLngPair[]): string {
  return JSON.stringify(points);
}
